import "./style.scss";

/**
 * Which dataset is this build talking to.
 *
 * The label is about the DATABASE, not about where the code runs — a local
 * dev server pointed at the team's backend is still LIVE data, and that is
 * exactly the case worth flagging.
 *
 * Set REACT_APP_ENV_LABEL explicitly wherever possible. The fallback infers
 * from the API host, which is reliable for the two deployed environments but
 * cannot know what database a localhost backend is configured against — so
 * an unlabelled localhost build says LOCAL API rather than guessing.
 */
type EnvKind = "live" | "lab" | "local";

const isLocalHost = () => {
  const host = window.location.hostname;
  return (
    host === "localhost" || host === "127.0.0.1" || host.startsWith("192.168.")
  );
};

const resolveEnv = (): { kind: EnvKind; label: string } | null => {
  const explicit = process.env.REACT_APP_ENV_LABEL?.trim().toLowerCase();
  if (explicit === "none" || explicit === "off") return null;

  // An explicit label wins anywhere, including a deployed build — that is the
  // switch to turn the badge on for a hosted environment if it is ever wanted.
  if (explicit === "live") return { kind: "live", label: "LIVE DATA" };
  if (explicit === "lab") return { kind: "lab", label: "LAB DATA" };

  // Otherwise the badge is a local-run device only. Each deployed environment
  // has its own hostname, which already says where you are.
  if (!isLocalHost()) return null;

  const apiUrl = (process.env.REACT_APP_API_URL || "").toLowerCase();
  if (apiUrl.includes("zrs-hubtech")) return { kind: "lab", label: "LAB DATA" };
  if (apiUrl.includes("medhubeg.com")) return { kind: "live", label: "LIVE DATA" };

  // A localhost API: the frontend cannot know which database that backend is
  // pointed at, so say so rather than guess.
  return { kind: "local", label: "LOCAL API" };
};

export const EnvBadge = () => {
  const env = resolveEnv();
  if (!env) return null;

  return (
    <span
      className={`env-badge env-badge-${env.kind}`}
      title={`API: ${process.env.REACT_APP_API_URL || window.location.origin}`}
    >
      {env.label}
    </span>
  );
};

export default EnvBadge;
